import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/shared/ScrollToTop";
import { EditModeProvider } from "@/contexts/EditModeContext";
import AnalyticsProvider from "@/components/common/AnalyticsProvider";
import axios from "axios";

// Public Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import NewServicesPage from "@/pages/NewServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import WorkshopTopicPage from "@/pages/WorkshopTopicPage";
import WorkshopRequestPage from "@/pages/WorkshopRequestPage";
import WorkshopsPage from "@/pages/WorkshopsPage";
import CoachingServicePage from "@/pages/CoachingServicePage";
import CareerStrategySessionPage from "@/pages/CareerStrategySessionPage";
import CareerCoachingPackagesPage from "@/pages/CareerCoachingPackagesPage";
import FreeResourcesPage from "@/pages/FreeResourcesPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactPage from "@/pages/ContactPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogEditor from "@/pages/admin/AdminBlogEditor";
import AdminServices from "@/pages/admin/AdminServices";
import AdminCoachingServices from "@/pages/admin/AdminCoachingServices";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminSlides from "@/pages/admin/AdminSlides";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminResources from "@/pages/admin/AdminResources";
import AdminPages from "@/pages/admin/AdminPages";
import AdminFAQ from "@/pages/admin/AdminFAQ";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminStats from "@/pages/admin/AdminStats";
import AdminABTests from "@/pages/admin/AdminABTests";
import AdminComponentLibrary from "@/pages/admin/AdminComponentLibrary";
import AdminHelp from "@/pages/admin/AdminHelp";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminKnowledgeBase from "@/pages/admin/AdminKnowledgeBase";
import AdminWorkshops from "@/pages/admin/AdminWorkshops";
import AdminWorkshopAttendance from "@/pages/admin/AdminWorkshopAttendance";
import FullPageEditor from "@/pages/admin/FullPageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    // Fetch site settings for analytics
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/site-settings`);
        setSiteSettings(res.data);
        // Store tracking ID globally for page view tracking
        if (res.data.ga_tracking_id) {
          window.GA_TRACKING_ID = res.data.ga_tracking_id;
        }
      } catch (error) {
        console.log('Could not load site settings for analytics');
      }
    };
    fetchSettings();
  }, []);

  return (
    <BrowserRouter>
      <EditModeProvider>
        <ScrollToTop />
        <Toaster position="top-right" richColors />
        <AnalyticsProvider settings={siteSettings} />
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<NewServicesPage />} />
        <Route path="/services-old" element={<ServicesPage />} />
        <Route path="/workshops" element={<WorkshopsPage />} />
        <Route path="/service/workshop" element={<Navigate to="/workshops" replace />} />
        <Route path="/workshop/request" element={<WorkshopRequestPage />} />
        <Route path="/workshop/:slug" element={<WorkshopTopicPage />} />
        <Route path="/service/:slug" element={<ServiceDetailPage />} />
        <Route path="/coaching/career-strategy-session" element={<CareerStrategySessionPage />} />
        <Route path="/coaching/career-coaching-packages" element={<CareerCoachingPackagesPage />} />
        <Route path="/coaching-packages" element={<CareerCoachingPackagesPage />} />
        <Route path="/coaching/:slug" element={<CoachingServicePage />} />
        <Route path="/free-resources" element={<FreeResourcesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/contact-us" element={<ContactPage />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="blog/new" element={<AdminBlogEditor />} />
          <Route path="blog/edit/:id" element={<AdminBlogEditor />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="coaching-services" element={<AdminCoachingServices />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="slides" element={<AdminSlides />} />
          <Route path="submissions" element={<AdminSubmissions />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="resources" element={<AdminResources />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="pages/edit/:id" element={<FullPageEditor />} />
          <Route path="faq" element={<AdminFAQ />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="stats" element={<AdminStats />} />
          <Route path="ab-tests" element={<AdminABTests />} />
          <Route path="components" element={<AdminComponentLibrary />} />
          <Route path="help" element={<AdminHelp />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="transactions" element={<AdminTransactions />} />
          <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
          <Route path="workshop-management" element={<AdminWorkshops />} />
          <Route path="workshop-attendance" element={<AdminWorkshopAttendance />} />
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </EditModeProvider>
    </BrowserRouter>
  );
}

export default App;
